import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { company, media } from '../data/site';
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.72, 0.9]);
  return <section id="home" ref={ref} className="relative isolate w-full overflow-hidden bg-navy-900" data-id="element-248" data-mp="xb5qnbykqnuo">
      <motion.div style={{
      y,
      scale
    }} className="absolute inset-0 -z-10" data-id="element-249" data-mp="s9giq56neqat">
        <img src={media.hero} alt="Custom home built by Pure Build Construction at dusk" className="h-full w-full object-cover" data-id="element-250" data-mp="nbez9j5b8oyt" />
      </motion.div>
      <motion.div style={{
      opacity: overlay
    }} className="absolute inset-0 -z-10 bg-navy-900" aria-hidden="true" data-id="element-251" data-mp="381e3bhj8idw5" />
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,rgba(5,13,24,0.92)_0%,rgba(5,13,24,0.6)_55%,rgba(5,13,24,0.35)_100%)]" aria-hidden="true" data-id="element-252" data-mp="3j9u0ky5t7mla" />
      <div className="grid-lines absolute inset-0 -z-10 opacity-60" aria-hidden="true" data-id="element-253" data-mp="399bavmrkdbvg" />

      <div className="mx-auto flex min-h-[92vh] max-w-[1400px] flex-col justify-center px-5 pb-24 pt-40 sm:px-8 lg:pb-32 lg:pt-44" data-id="element-254" data-mp="3e8msexzr8le3">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.34em] text-brand-400" data-id="element-255" data-mp="30ggap7gye6cj">
          <span className="h-px w-10 bg-brand-500" aria-hidden="true" data-id="element-256" data-mp="2vi50gvmqxtts" />
          Pure Build Construction
        </motion.p>

        <div className="mt-8 overflow-hidden" data-id="element-257" data-mp="2hjowhcg9qhfc">
          <motion.h1 initial={{
          y: '110%'
        }} animate={{
          y: 0
        }} transition={{
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1]
        }} className="font-heading text-[13vw] font-extrabold uppercase leading-[0.92] tracking-[-0.035em] text-white sm:text-7xl lg:text-[6.5rem]" data-id="element-258" data-mp="2lkin4dl3iabx">
            Built Right.
          </motion.h1>
        </div>
        <div className="overflow-hidden" data-id="element-259" data-mp="i9vqb23hxwks">
          <motion.h1 initial={{
          y: '110%'
        }} animate={{
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.12,
          ease: [0.22, 1, 0.36, 1]
        }} className="font-heading text-[13vw] font-extrabold uppercase leading-[0.92] tracking-[-0.035em] text-white/35 sm:text-7xl lg:text-[6.5rem]" data-id="element-260" data-mp="ddwyt5mvfyg5">
            Built to Last.
          </motion.h1>
        </div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.35
      }} className="mt-9 max-w-xl text-lg leading-relaxed text-white/70" data-id="element-261" data-mp="x1w6u8q1wrhv">
          Quality construction backed by craftsmanship, attention to detail, and a commitment to doing the job right —
          for homeowners across {company.region}.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.5
      }} className="mt-11 flex flex-col gap-3 sm:flex-row" data-id="element-262" data-mp="21bfttl88j7sc">
          <a href="#contact" className="group inline-flex items-center justify-center gap-3 bg-brand-500 px-8 py-5 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-brand-600" data-id="element-263" data-mp="2b2k9yo9ncyor">
            Get a Free Estimate
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" data-id="element-264" data-mp="2p10i8jn5qx90" />
          </a>
          <a href="#work" className="group inline-flex items-center justify-center gap-3 border border-white/25 px-8 py-5 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:border-white hover:bg-white hover:text-navy" data-id="element-265" data-mp="umhfqh2m8tqz">
            View Our Work
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" data-id="element-266" data-mp="3vhxffh69x3xh" />
          </a>
        </motion.div>
      </div>

      <motion.a href="#about" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} className="absolute bottom-8 right-5 hidden items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/50 transition hover:text-white sm:right-8 lg:flex" data-id="element-267" data-mp="e77jvt5jjlkn">
        Scroll
        <motion.span animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity
      }} data-id="element-268" data-mp="3rh584tgyp93u">
          <ArrowDown className="h-4 w-4" aria-hidden="true" data-id="element-269" data-mp="3moc467tbi4st" />
        </motion.span>
      </motion.a>
    </section>;
}